import {
  AlertTriangle,
  CalendarCheck,
  CheckSquare,
  ClipboardList,
  RefreshCw,
  Sparkles,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/Skeleton';
import { formatDate } from '@/lib/formatters';
import { useDailyBrief, type BriefUrgency } from '@/features/dashboard/hooks';

/**
 * Full-width AI daily brief that sits at the top of /dashboard for every
 * member. The cash-runway banner (super_admin only) renders below this.
 */
export function DailyBriefBanner() {
  const { data, isLoading, isError, error, refetch, isFetching } = useDailyBrief();
  const brief = data?.brief;

  if (isLoading) {
    return (
      <div className="rounded-lg border border-border bg-card p-5">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-muted-foreground" />
          <Skeleton className="h-4 w-32" />
        </div>
        <Skeleton className="mt-3 h-8 w-2/3" />
        <Skeleton className="mt-3 h-4 w-full max-w-3xl" />
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-16" />
          ))}
        </div>
      </div>
    );
  }

  if (isError || !brief) {
    return (
      <div className="flex items-center justify-between gap-4 rounded-lg border border-border bg-card p-5">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Sparkles className="h-4 w-4" />
          <span>
            Couldn't write your daily brief right now
            {error instanceof Error ? ` — ${error.message}` : ''}.
          </span>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-50"
        >
          <RefreshCw className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')} />
          Retry
        </button>
      </div>
    );
  }

  const todayLabel = formatDate(new Date().toISOString(), 'long');
  const hasFocus = brief.focus.length > 0;
  const hasFigures = brief.figures.length > 0;
  const hasAlerts = brief.alerts.length > 0;
  const hasTasks = brief.tasks.length > 0;

  return (
    <div className="rounded-lg border border-info-soft/70 bg-gradient-to-br from-info-soft/35 via-info-soft/15 to-card p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs font-medium uppercase tracking-wide text-info">
            <Sparkles className="h-3.5 w-3.5" />
            Daily brief
            <span className="font-normal normal-case tracking-normal">· {todayLabel}</span>
          </div>

          <h2 className="text-2xl font-medium tracking-headline text-foreground sm:text-3xl">
            {brief.headline}
          </h2>

          {brief.summary && (
            <p className="max-w-3xl pt-1 text-sm text-foreground/80">{brief.summary}</p>
          )}
        </div>

        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          title="Re-run the brief"
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-border bg-card/60 px-2.5 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-50"
        >
          <RefreshCw className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')} />
          Refresh
        </button>
      </div>

      {hasFigures && (
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
          {brief.figures.map((f) => (
            <div
              key={`${f.label}-${f.value}`}
              className="rounded-md border border-border/70 bg-card/70 px-3 py-2.5"
            >
              <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {f.label}
              </p>
              <p className="mt-0.5 text-lg font-medium tabular-nums tracking-headline text-foreground">
                {f.value}
              </p>
              {f.detail && <p className="mt-0.5 text-xs text-muted-foreground">{f.detail}</p>}
            </div>
          ))}
        </div>
      )}

      {(hasFocus || hasTasks || hasAlerts) && (
        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
          {hasFocus && (
            <section>
              <SectionLabel icon={ClipboardList} text="Today's focus" />
              <ol className="mt-2 space-y-1.5">
                {brief.focus.map((item, i) => (
                  <li key={`${i}-${item}`} className="flex gap-2 text-sm text-foreground">
                    <span className="mt-0.5 w-4 shrink-0 text-xs font-medium tabular-nums text-muted-foreground">
                      {i + 1}.
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ol>
            </section>
          )}

          {hasTasks && (
            <section>
              <SectionLabel icon={CheckSquare} text="Your work" />
              <ul className="mt-2 space-y-1.5">
                {brief.tasks.map((t, i) => (
                  <li key={`${i}-${t.label}`} className="flex items-start gap-2 text-sm">
                    <UrgencyDot urgency={t.urgency} />
                    <div className="min-w-0">
                      <p className="text-foreground">{t.label}</p>
                      {t.detail && <p className="text-xs text-muted-foreground">{t.detail}</p>}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {hasAlerts && (
            <section>
              <SectionLabel icon={AlertTriangle} text="Watch-outs" />
              <ul className="mt-2 space-y-1.5">
                {brief.alerts.map((a, i) => (
                  <li key={`${i}-${a.label}`} className="flex items-start gap-2 text-sm">
                    <UrgencyDot urgency={a.urgency} />
                    <div className="min-w-0">
                      <p className="text-foreground">{a.label}</p>
                      <p className="text-xs text-muted-foreground">{a.detail}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}

      {!hasFocus && !hasFigures && !hasAlerts && !hasTasks && (
        <p className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
          <CalendarCheck className="h-4 w-4" />
          Quiet morning — nothing urgent in Atlas right now.
        </p>
      )}
    </div>
  );
}

function SectionLabel({
  icon: Icon,
  text,
}: {
  icon: typeof ClipboardList;
  text: string;
}) {
  return (
    <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
      <Icon className="h-3.5 w-3.5" />
      {text}
    </div>
  );
}

function UrgencyDot({ urgency }: { urgency: BriefUrgency | null }) {
  return (
    <span
      className={cn(
        'mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full',
        urgency === 'high'
          ? 'bg-fresh-salmon-dark'
          : urgency === 'medium'
            ? 'bg-atmosphere-dark'
            : 'bg-muted-foreground/40',
      )}
      aria-hidden
    />
  );
}
